import { useState, useEffect, useRef, useMemo } from "react";
import PlayerSlot from "../playerSlot/playerSlot";
import transformSlot from "../functions";
import PitchSVG from "./pitchSVG";
import "./pitchView.sass";

const MOBILE_BREAKPOINT = 480;

/**
 * Componente PitchView - Renderiza la cancha de fútbol con posiciones de jugadores
 * 
 * Muestra una cancha de fútbol responsiva (vertical en mobile, horizontal en desktop)
 * con líneas de campo, áreas, arcos y slots para jugadores. Se adapta dinámicamente
 * a cambios de orientación usando ResizeObserver.
 * 
 * @component
 * @example
 * const slots = FORMATIONS["4-3-3"].slots;
 * const assignments = { gk: player1, rb: player2, ... };
 * 
 * return (
 *   <PitchView
 *     slots={slots}
 *     assignments={assignments}
 *     activeSlotId="gk"
 *     onSlotClick={(slotId) => console.log(slotId)}
 *     onClear={(slotId) => console.log("cleared", slotId)}
 *     accentColor="#FF0000"
 *     pitchColor={["#578729", "#527E25"]}
 *     nameColor="#FFFFFF"
 *     lastNameColor="#FFFFFF"
 *     draggingPlayer={null}
 *     teamImage="https://example.com/team.png"
 *     backgroundTeam="#E8E8E8"
 *   />
 * );
 * 
 * @param {Object} props - Props del componente
 * @param {Array<Object>} props.slots - Array de slots de la formación
 *   @param {string} props.slots[].id - ID único del slot (ej: "gk", "rb")
 *   @param {string} props.slots[].label - Etiqueta visible (ej: "POR", "LD")
 *   @param {string} props.slots[].role - Rol genérico (ej: "Portero", "Defensa")
 *   @param {string} props.slots[].roleKey - Rol específico para filtrado
 *   @param {number} props.slots[].x - Posición X porcentual (0-100)
 *   @param {number} props.slots[].y - Posición Y porcentual (0-100)
 * @param {Object<string, Object>} props.assignments - Mapa de slotId → jugador asignado
 *   @param {string} props.assignments[slotId].id - ID del jugador
 *   @param {string} props.assignments[slotId].name - Nombre del jugador
 *   @param {string} props.assignments[slotId].photo - URL de foto del jugador
 * @param {string} props.activeSlotId - ID del slot actualmente seleccionado
 * @param {Function} props.onSlotClick - Callback cuando se hace click en un slot
 *   @param {string} slotId - ID del slot clickeado
 * @param {Function} props.onClear - Callback cuando se limpia un slot
 *   @param {string} slotId - ID del slot a limpiar
 * @param {string} props.accentColor - Color de acento para elementos activos (hex)
 * @param {Array<string>} props.pitchColor - Array [colorClaro, colorOscuro] para gradiente
 *   @example ["#578729", "#527E25"]
 * @param {string} props.nameColor - Color del nombre del jugador (hex)
 * @param {string} props.lastNameColor - Color del apellido del jugador (hex)
 * @param {Object|null} props.draggingPlayer - Jugador siendo arrastrado o null
 *   @param {string} props.draggingPlayer.id - ID del jugador
 *   @param {string} props.draggingPlayer.name - Nombre del jugador
 * @param {string} props.teamImage - URL de imagen del equipo (fallback cuando no hay jugador)
 * @param {string} props.backgroundTeam - Color de fondo del círculo del avatar
 * 
 * @returns {JSX.Element} Elemento SVG con cancha y slots de jugadores
 * 
 * @see PlayerSlot - Componente individual de slot
 * @see PitchSVG - Componente que renderiza las líneas de la cancha
 * @see transformSlot - Función que transforma coordenadas según orientación
 */
export default function PitchView({ slots, assignments, activeSlotId, onSlotClick, onClear, accentColor, pitchColor, nameColor, lastNameColor, draggingPlayer, teamImage, backgroundTeam, selectionPath }) {
  const [isHorizontal, setIsHorizontal] = useState(false);
  const wrapperRef = useRef(null);

  // Detecta el breakpoint observando el ancho real del wrapper
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const check = () => setIsHorizontal(window.innerWidth > MOBILE_BREAKPOINT);
    check();

    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, []);

  const pitchGradient = useMemo(() => {
    if (!pitchColor) return undefined;
    return `linear-gradient(180deg, ${pitchColor[0]} 0%, ${pitchColor[1]} 25%, ${pitchColor[0]} 50%, ${pitchColor[1]} 75%, ${pitchColor[0]} 100%)`;
  }, [pitchColor]);

  return (
    <div className={`pitch-view-wrapper ${isHorizontal ? "pitch-view-wrapper--horizontal" : ""}`} ref={wrapperRef}>
      <div className="pitch-view"
        style={pitchGradient ? { background: pitchGradient } : undefined}
      >
        {/* Líneas SVG — se adaptan según orientación */}
        <PitchSVG isHorizontal={isHorizontal} />

        {slots.map((slot, index) => {
          const transformedSlot = transformSlot(slot, isHorizontal);
          return (
            <PlayerSlot
              {...{onClear, draggingPlayer, lastNameColor, nameColor, accentColor, teamImage, backgroundTeam, selectionPath}}
              key={slot.id}
              slot={transformedSlot}
              slotNumber={index + 1}
              player={assignments[slot.id] || null}
              isActive={activeSlotId === slot.id}
              onClick={() => onSlotClick(slot.id)}
            />
          );
        })}
      </div>
    </div>
  );
}